import React, { useEffect, useRef } from "react"

import showModal from "./Modal"
import SectionHeader from '../molecules/SectionHeader'
import Label from '../atoms/Label'
import { markdownToJSX } from '../../util/transforms'
import { ITEM_HTML_MODAL, ITEM_HTML } from '../../api/contentTypes'
import { MEDIUM } from '../util/breakpoints'
import useMediaQuery from '../util/useMediaQuery'
import addToHead from '../util/addToHead'
import useSiteData from "../../useSiteData"

// Helper used by HtmlModal and Html to add optional Label, Title and Description
const SnippetHeader = ({ label, title, description }) => {

  if( !label && !title && !description ) {
    return null
  }

  return (
    <div className="o-html-snippet-header">
      { label && <Label text={ label }/> }
      { title && <h2>{ markdownToJSX(title) }</h2> }
      { description && <p>{ markdownToJSX(description) }</p> }
    </div>
  )

}

// corresponds to Content Model - Item: Html Snippet - itemHtmlSnippet
const Html = ({ label, title, description, html, addToDocumentHead }) => {

  addToHead( addToDocumentHead )

  const HTMLSnippet = ( <div className="o-html-snippet-html" dangerouslySetInnerHTML={{ __html: html }}></div> );

  return (
    <>
      <SnippetHeader label={ label } title={ title } description={ description } />
      { HTMLSnippet }
    </>
  )

}

// corresponds to Content Model - Item: Html Snippet in Modal - itemHtmlSnippetModal
const HtmlModal = ({ label, title, description, openModalCta, html, addToDocumentHead, modalApperance, centerCtaButton }) => {

  addToHead( addToDocumentHead )

  const HTMLSnippet = ( <div className="o-html-snippet-html" dangerouslySetInnerHTML={{ __html: html }}></div> )

  // If video is set to true when calling showModal a black background is added to mobile. We avoid that by useMediaQuery
  const isDesktop = useMediaQuery({ minWidth: MEDIUM })
  const { translations } = useSiteData().pageSettings


  const openModal = () => {
    if( isDesktop ) {
      showModal({ children: HTMLSnippet, video: modalApperance == 'Wide' ? true : false, translations  })
    } else {
      showModal({ children: HTMLSnippet, translations })
    }
  }

  return (
    <>
      <SnippetHeader label={ label } title={ title } description={ description } />
      <div  className={'o-html-snippet-cta' + (centerCtaButton ? ' o-html-snippet-cta--center' : '')}>
        <button onClick={ openModal }>{ openModalCta }</button>
      </div>
    </>
  )

}

// corresponds to Content Model - Block: Html snippet - htmlSnippet
export default ({ label, title, intro, htmlSnippet = [] }) => {

  // dangerouslySetInnerHTML sets innerHTML which does not allow scripts to be executed
  // so once the component is mounted we find all scripts and use createElement to create
  // a new dom element that replaces the script tag that was not executed
  // https://stackoverflow.com/questions/1197575/can-scripts-be-inserted-with-innerhtml/69190644#69190644
  const ref = useRef( null )
  useEffect(()=>{
    const scriptElements = ref.current.querySelectorAll("script")
    Array.from(scriptElements).forEach((scriptElement) => {
      const clonedElement = document.createElement("script")

      Array.from(scriptElement.attributes).forEach((attribute) => {
        clonedElement.setAttribute(attribute.name, attribute.value)
      })
      
      clonedElement.text = scriptElement.text

      scriptElement.parentNode.replaceChild(clonedElement, scriptElement)
    })
  }, [])

  const htmlSnippetElements = htmlSnippet.map(( itemData, index ) => {

    switch ( itemData.contentType ) {
      case ITEM_HTML_MODAL : 
        return (
          <div className="o-html-snippet-grid__item" key={ index }>
            <HtmlModal key={ index } { ...itemData } />
          </div>
      )
      case ITEM_HTML : 
        return (
          <div className="o-html-snippet-grid__item" key={ index }>
            <Html key={ index } { ...itemData } />
        </div>
      )
    }

  })

  return (
    <>
      <section className={ "u-container u-section-spacing u-container--large o-html-snippet" } >

        { ( label || title || intro ) &&
          <SectionHeader 
            title={ markdownToJSX(title) } 
            label={ label } 
            paragraph={ markdownToJSX(intro) } 
            isCentred={ true }
            wide={ false } />
        }

        <div className="o-html-snippet-grid u-container--large" ref={ ref }>
          { htmlSnippetElements }
        </div>

      </section>
    </>
  )

}